import React from 'react';
import { motion } from 'framer-motion';
import CallButton from '../components/CallButton';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { eventsData } from '../data/eventsData';
import { getEventStatus } from '../utils/eventHelpers';
import { format, parseISO } from 'date-fns';
import { useLanguage } from '../context/LanguageContext';

const { FiCalendar, FiClock } = FiIcons;

export default function PoolEvents() {
  const { t, lang } = useLanguage();

  return (
    <div className="container mx-auto px-4 py-16 max-w-6xl">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-16">
        <h1 className="text-5xl font-bold text-brand-navy mb-4">{t('poolEvents.title')}</h1>
        <div className="h-1 w-24 bg-brand-red mx-auto rounded mb-6"></div>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto">{t('poolEvents.subtitle')}</p>
      </motion.div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-24">
        {eventsData.map((event, idx) => {
          const status = getEventStatus(event.startDate, event.endDate);
          const isSingleDay = !event.endDate || event.startDate === event.endDate;
          const statusLabel = lang === 'en' ? status.label : (status.label === 'Ongoing' ? 'En cours' : status.label === 'Upcoming' ? 'À venir' : 'Terminé');

          return (
            <motion.div key={event.id} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }} className={`relative bg-white rounded-3xl p-8 border border-gray-100 shadow-xl overflow-hidden hover:shadow-2xl transition-all ${status.label === 'Expired' ? 'opacity-60 grayscale' : ''}`}>
              <div className="flex justify-between items-start mb-6">
                <span className={`${status.color} text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest shadow-sm`}>
                  {statusLabel}
                </span>
                <span className="text-brand-gold font-bold text-sm uppercase">{event.type[lang]}</span>
              </div>
              <h3 className="text-2xl font-bold text-brand-navy mb-4">{event.title[lang]}</h3>
              <p className="text-gray-600 mb-8 line-clamp-2">{event.description[lang]}</p>
              <div className="space-y-3 border-t border-gray-50 pt-6">
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <SafeIcon icon={FiCalendar} className="text-brand-red" />
                  <span>
                    {format(parseISO(event.startDate), 'MMM do, yyyy')}
                    {!isSingleDay && ` - ${format(parseISO(event.endDate), 'MMM do, yyyy')}`}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <SafeIcon icon={FiClock} className="text-brand-red" />
                  <span>{t('poolEvents.startsAt')}</span>
                </div>
              </div>
              {status.label !== 'Expired' && (
                <div className="mt-8">
                  <CallButton className="w-full text-sm py-2" />
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
      {/* Info Section */}
      <div className="bg-gray-50 rounded-[3rem] p-8 md:p-16 border border-gray-200">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          <div className="lg:w-1/2">
            <h2 className="text-3xl font-bold text-brand-navy mb-6">{t('poolEvents.hostTitle')}</h2>
            <p className="text-gray-600 text-lg mb-8 leading-relaxed">
              {t('poolEvents.hostText')}
            </p>
            <ul className="space-y-4 mb-8">
              {t('poolEvents.features').map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-gray-700 font-medium">
                  <div className="h-2 w-2 rounded-full bg-brand-gold" />
                  {item}
                </li>
              ))}
            </ul>
            <CallButton />
          </div>
          <div className="lg:w-1/2 w-full">
            <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
              <img src="https://images.unsplash.com/photo-1574163012975-f5be8e9d4076?auto=format&fit=crop&q=80&w=800" alt="Event Space" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}